import { SettingsManager, Utils } from "@viamap/viamap2-common";
import React from "react";
import { MapitUtils } from "src/managers/MapitUtils";
// import { BNBOFunc } from "../BNBOModule/BNBOFunc";
import { FeatureAllowDenyListMap } from "src/states/ApplicationStateFeatureMapping";
import { Feature } from "src/states/ApplicationState";
import { FRPProjectPersistence } from "./FRPProjectPersistence";
import { FRPElementPersistence } from "./FRPElementPersistence";
import { MitStorage } from "src/managers/MitStorage";
import { stringify as wktStringify } from 'wkt';
import * as Turf from '@turf/turf';
import chroma from "chroma-js";
import { FRPConst } from "./FRPConst";
import { FRPFunc } from "./FRPFunc";

export enum MessageType {
    Error = "Error",
    Success = "Success"
}

export enum FRPUserAccessRights {
    "Read" = "Read",
    "Write" = "Write",
    "Admin" = "Admin"
}

/**
* User Access Rights to Project Access Parameters
*/
export const FeaturesAccessibleFRPUserAccessRights: FeatureAllowDenyListMap = {
    [FRPUserAccessRights.Read]: {
        allow: [Feature.FRPProjectRead],
        deny: [Feature.FRPProjectWrite, Feature.FRPProjectAdmin]
    },
    [FRPUserAccessRights.Write]: {
        allow: [Feature.FRPProjectRead, Feature.FRPProjectWrite],
        deny: [Feature.FRPProjectAdmin]
    },
    [FRPUserAccessRights.Admin]: {
        allow: [Feature.FRPProjectRead, Feature.FRPProjectWrite, Feature.FRPProjectAdmin],
        deny: []
    },
};

export interface FRPProjectPersistenceInterface {
    listObjects(filter: any): Promise<FRPProject[]>;
    getObject(objectId: string): Promise<FRPProject>;
    saveObject(object: FRPProject): Promise<void>;
    deleteObject(objectId: string): Promise<void>;
    createObject(object: FRPProject): Promise<void>;
    createProject(project: FRPProject): Promise<void>;
};

export interface FRPElementPersistenceInterface {
    listObjects(filter: any): Promise<FRPElement[]>;
    getObject(objectId: string): Promise<FRPElement>;
    saveObject(object: FRPElement): Promise<void>;
    deleteObject(objectId: string): Promise<void>;
    createObject(object: FRPElement): Promise<void>
}

export const FRPLocalStorageKey = "FRPState-LocalStorage";
export type FRPNotificationState = {
    [esIndexKey: string]: { // Last time an element was interacted with by local user
        [elementID: string]: Date,
    };
}
export const FRPLocalUserSettingsKey = "FRPState-LocalUserSettings";
export type FRPLocalUserSettings = {
    [view: `${string}-sort`]: {
        dir: "asc" | "desc";
        key: string;
    };
};

export type TurfFeature = any; // for documentation only.
export type geoJsonMultipolygon = any;

export enum FRPElementType {
    Element = "Element", // A generic element, e.g. a point, line or polygon
    Message = "Message", // A message related to an element
    Action = "Action", // An action related to an element
}
export type FRPElementRegister = { [elementId: string]: FRPElement }

export type FRPActionArray = FRPAction[];
export type FRPMessageArray = FRPMessage[];


export interface FRPProject {
    id: string;
    name: string;
    description: string;
    active: boolean;
    ownerUser: string; // User id of the owner. E.g. kbe@viamap.net
    ownerOrganization: string; // Organization of the owner. E.g. viamap.net
    allowedUsersWithRoles: { [userId: string]: string[] }; // E.g. {"kbe@viamap.net":["BNBO","BNBOAdmin","BNBOWriteAcess"],"mbj@molbak.dk":["BNBO","BNBOWriteAcess"]}
    allowedOrganizationsWithRoles: { [organizationId: string]: string[] }; // E.g. {"viamap.net":["BNBO"],"molbak.dk":["BNBO", "BNBOWriteAcess"]}
    settings: any;
    esIndexName: string;
    _version?: number; // Version number of the document when read from the data store
    protected: boolean; // If true, the project is protected from deletion or bulk update
}

export interface FRPElement {
    id: string;
    _version?: number; // Version number of the document when read from the data store
    type: FRPElementType.Element;
    name?: string; // The name of the element, e.g. "My Point", "My Line", "My Polygon"
    geometry?: TurfFeature; // The geometry of the element
    documents: { [documentId: string]: any }; // Documents related to the element, e.g. {"doc1": {name: "doc1.pdf", url: "https://example.com/doc1.pdf"}}
    lastChangedTimestamp: Date; // The last time the element was changed
    lastChangedUser: string; // The user who last changed the element
    description?: string,
    status: string; // The status of the element, e.g. "active", "inactive", "deleted"
    tags: string[]; // Tags related to the element, e.g. ["tag1", "tag2"]
    expectedStartDate?: string; // The expected start date of the element
    expectedEndDate?: string; // The expected end date of the element
    refElementId?: string; // CHANGE TO parentRefId // The id of the element this element is related to or undefined if not related to an element  
}


export type FRPMessage = {
    id: string;
    _version?: number; // Version number of the document when read from the data store
    type: FRPElementType.Message;
    elementId: string | undefined; // The id of the element this message is related to or undefined if not related to an element
    content: string; // The content of the message
    user: string; // The user who created the message, e.g. "'
    created: Date; // The date the message was created
    edited?: Date; // The date the message was last edited
}

export enum FRPActionType {
    Create = "create",
    Update = "update"
}

export type FRPAction = {
    id: string;
    _version?: number; // Version number of the document when read from the data store
    type: FRPElementType.Action;
    elementId: string | undefined; // The id of the element this action is related to or undefined if not related to an element
    user: string; // The user who created the action, e.g. "
    date: Date; // The date the action was created
    action: FRPActionType; // The action performed, e.g. "create", "update", "delete",
    message?: string; // An optional message related to the action
}

export type FRPVisualizationId = string;
export type FRPVisualization = { id: FRPVisualizationId, da: string, en: string, inactive: boolean, access: string };

export type FRPActionStatus = number;
// ---------------------------------------------------- STATE --------------------------------------------------------------



export type LoadedFRPState = any;
export interface FRPState {
    stateVersion: 1,
    activeTransactions: { [guid: string]: any },
    message: string,
    messageType: MessageType,

    isInitialized: boolean;

    availableProjectsList: FRPProject[];
    selectedProjectId?: string;
    selectedProject?: FRPProject;

    loadedProjectId?: string; // Set when all elements are loaded for the selected project

    elements: FRPElementRegister;

    messages: FRPMessageArray; // Messages related to the elements
    actions: FRPActionArray; // Actions related to the elements
    statusOptions: { id: string, val: string, color: string, status: FRPActionStatus }[];
    tagOptions: { id: string, val: string, color: string }[];
    childrenLookupTable: { [parentRefId: string]: string[] };
    view: 'project' | 'element' | 'custom' | 'none'; // Custom and none might not be used. But might be relevant for the future
    shownElementsIds: string[];
    selectedElementIds: string[];
    filter: { [key: string]: any[] };
    visualizationId: FRPVisualizationId;
}

// ---------------------------------------------------- INITIAL STATE --------------------------------------------------------------

export function initialFRPState(): FRPState {

    return {
        stateVersion: 1, //Increment, when requiredFields are added
        activeTransactions: {},
        message: "",
        messageType: MessageType.Success,

        isInitialized: false,

        availableProjectsList: [],

        elements: {},
        actions: [],
        messages: [],
        statusOptions: [
            { id: "1", val: "Ny", color: "rgba(245, 196, 27, 0.6)", status: 0 },
            { id: "2", val: "Afvanter opstart samatale", color: "rgba(243, 156, 18, 0.6)", status: 10 },
            { id: "3", val: "Måligner startet", color: "rgba(155, 89, 182, 0.6)", status: 30 },
            { id: "4", val: "Målinger indset til ks", color: "rgba(230, 126, 34, 0.6)", status: 70 },
            { id: "5", val: "Færdig", color: "rgba(104, 204, 69, 0.6)", status: 100 }
        ],
        tagOptions: [
            { id: "1", val: "Infrastruktur", color: "rgba(52, 152, 219, 0.6)" },
            { id: "2", val: "Miljø", color: "rgba(39, 174, 96, 0.6)" },
            { id: "3", val: "Bolig", color: "rgba(155, 89, 182, 0.6)" },
            { id: "4", val: "Erhverv", color: "rgba(230, 126, 34, 0.6)" },
            { id: "5", val: "Offentligt rum", color: "rgba(41, 128, 185, 0.6)" },
            { id: "6", val: "Vej", color: "rgba(192, 57, 43, 0.6)" },
            { id: "7", val: "Bygning", color: "rgba(243, 156, 18, 0.6)" },
            { id: "8", val: "Park", color: "rgba(46, 204, 113, 0.6)" },
        ],
        childrenLookupTable: {},
        view: 'project',
        shownElementsIds: [],
        selectedElementIds: [],
        filter: {},
        visualizationId: FRPConst.visualizations[0].id
    };
}


export function initialNotificationFRPState(): FRPNotificationState {
    return MitStorage.getValue(FRPLocalStorageKey) || {} as FRPNotificationState;
}

export type SFEElem = { addr: string, sfeNummer: number, status: string }

// ---------------------------------------------------- ACTIONS --------------------------------------------------------------
export enum FRPActionType {
    TxStarted,
    TxCompleted,
    TxFailed,

    Initialize,

    LoadSFE,
    LoadProjectState,

    // Project Actions
    AddProject,
    RevertAddProject,
    UpdateProject,
    DeleteProject,
    SelectProject,
    LoadProjects,

    // Element Actions
    AddElement,
    DeleteElement,

    // Status and Tag Actions
    DeleteStatus,
    AddStatus,
    UpdateStatus,
    SetStatus,
    SetTag,

    SetView,
    SetShownElementsIds,
    SetSelectedElementIds,
    SetFilter,
    SetVisualizationId,

    CleanProjectState,

}

export const BroadcastedChanges = [
    FRPActionType.AddElement,
    FRPActionType.DeleteElement,
    FRPActionType.UpdateProject,
]

export interface TxStarted {
    type: FRPActionType.TxStarted;
    payload: { guid: string, action: FRPActions };
}

export interface TxCompleted {
    type: FRPActionType.TxCompleted;
    payload: { guid: string, action: FRPActions };
}

export interface TxFailed {
    type: FRPActionType.TxFailed;
    payload: { guid: string, action: FRPActions, error: any };
}


export interface Initialize {
    type: FRPActionType.Initialize;
    payload: {
        projectsElasticIndex: string,
        userId: string
    };
    results?: {
        projects: FRPProject[],
        projectsPersistence: FRPProjectPersistenceInterface
    };
}
export interface LoadSFE {
    type: FRPActionType.LoadSFE;
    payload: { sfeNummer: number };
    results?: any;
}


export interface LoadProjectState {
    type: FRPActionType.LoadProjectState;
    payload: {
        indexName: string,
        callback?: (info: any) => void,
        progressCallback?: (progressPct: number) => void,
        userId?: string
    }
    results?: any; // loaded data
    errorCallback?: (error: any) => void,
}

export interface AddProject {
    type: FRPActionType.AddProject;
    payload: { id: string, item: FRPProject }
}
export interface RevertAddProject {
    type: FRPActionType.RevertAddProject;
    payload: { id: string };
}
export interface UpdateProject {
    type: FRPActionType.UpdateProject;
    payload: { id: string, item: FRPProject, versionNumberWhenRead?: number }
}
export interface DeleteProject {
    type: FRPActionType.DeleteProject;
    payload: { id: string, versionNumberWhenRead?: number }
}
export interface SelectProject {
    type: FRPActionType.SelectProject;
    payload: { id: string }
}
export interface LoadProjects {
    type: FRPActionType.LoadProjects;
    results?: any; // loaded data
    callback?: (availableProjectsList: FRPProject[]) => void;
}
export interface AddElement {
    type: FRPActionType.AddElement;
    payload: { id: string, item: FRPElement | FRPAction | FRPMessage }
}

export interface DeleteElement {
    type: FRPActionType.DeleteElement;
    payload: { ids: string[] | string }
}
export interface DeleteStatus {
    type: FRPActionType.DeleteStatus;
    payload: { id: FRPState["statusOptions"][number]["id"] }
}
export interface AddStatus {
    type: FRPActionType.AddStatus;
    payload: { item: FRPState["statusOptions"][number] }
}

export interface UpdateStatus {
    type: FRPActionType.UpdateStatus;
    payload: { item: FRPState["statusOptions"][number] }
}

export interface SetStatus {
    type: FRPActionType.SetStatus;
    payload: { item: FRPState["statusOptions"] }
}

export interface SetTag {
    type: FRPActionType.SetTag;
    payload: { item: FRPState["tagOptions"] }
}

export interface SetView {
    type: FRPActionType.SetView;
    payload: { view: FRPState["view"] }
}
export interface SetShownElementsIds {
    type: FRPActionType.SetShownElementsIds;
    payload: { shownElementsIds: FRPState["shownElementsIds"] }
}
export interface SetSelectedElementIds {
    type: FRPActionType.SetSelectedElementIds;
    payload: { selectedElementIds: FRPState["selectedElementIds"] }
}
export interface SetFilter {
    type: FRPActionType.SetFilter;
    payload: { filter: FRPState["filter"] }
}

export interface SetVisualizationId {
    type: FRPActionType.SetVisualizationId;
    payload: { visualizationId: FRPState["visualizationId"] }
}

export interface CleanProjectState {
    type: FRPActionType.CleanProjectState;
}



// -------------------- utility functions to create an Action object ---------------------------------

export const actionTxStarted = (guid: string, action: FRPActions): TxStarted => ({
    type: FRPActionType.TxStarted,
    payload: { guid, action }
});

export const actionTxCompleted = (guid: string, action: FRPActions): TxCompleted => ({
    type: FRPActionType.TxCompleted,
    payload: { guid, action }
});

export const actionTxFailed = (guid: string, action: FRPActions, error: any): TxFailed => ({
    type: FRPActionType.TxFailed,
    payload: { guid, action, error }
});

export const initialize = (projectsElasticIndex: string, userId: string): Initialize => ({
    type: FRPActionType.Initialize,
    payload: { projectsElasticIndex, userId }
});

export const loadSFE = (sfeNummer: number): LoadSFE => ({
    type: FRPActionType.LoadSFE,
    payload: { sfeNummer }
});

export const loadProjectState = (indexName: string, userId?: string, callback?: (info: any) => void, errorCallback?: (error: any) => void, progressCallback?: (progressPct: number) => void): LoadProjectState => ({
    type: FRPActionType.LoadProjectState,
    payload: { indexName, callback, progressCallback, userId },
    errorCallback: errorCallback
})

export const addProject = (id: string, item: FRPProject): AddProject => ({
    type: FRPActionType.AddProject,
    payload: { id, item }
})
export const revertAddProject = (id: string): RevertAddProject => ({
    type: FRPActionType.RevertAddProject,
    payload: { id }
})
export const updateProject = (id: string, item: FRPProject, versionNumberWhenRead?: number): UpdateProject => ({
    type: FRPActionType.UpdateProject,
    payload: { id, item, versionNumberWhenRead }
})
export const deleteProject = (id: string, versionNumberWhenRead?: number): DeleteProject => ({
    type: FRPActionType.DeleteProject,
    payload: { id, versionNumberWhenRead }
})
export const selectProject = (id: string): SelectProject => ({
    type: FRPActionType.SelectProject,
    payload: { id }
})
export const loadProjects = (callback?: (info) => any): LoadProjects => ({
    type: FRPActionType.LoadProjects,
    callback: callback
})

export const addElement = (id: string, element: FRPElement | FRPAction | FRPMessage): AddElement => ({
    type: FRPActionType.AddElement,
    payload: { id: id, item: element }
})

export const deleteElements = (ids: string[] | string): DeleteElement => ({
    type: FRPActionType.DeleteElement,
    payload: { ids }
});

export const deleteStatus = (id: string): DeleteStatus => ({
    type: FRPActionType.DeleteStatus,
    payload: { id }
})

export const addStatus = (item: FRPState["statusOptions"][number]): AddStatus => ({
    type: FRPActionType.AddStatus,
    payload: { item }
})

export const updateStatus = (item: FRPState["statusOptions"][number]): UpdateStatus => ({
    type: FRPActionType.UpdateStatus,
    payload: { item }
})

export const setStatus = (item: FRPState["statusOptions"]): SetStatus => ({
    type: FRPActionType.SetStatus,
    payload: { item }
})

export const setTag = (item: FRPState["tagOptions"]): SetTag => ({
    type: FRPActionType.SetTag,
    payload: { item }
})

export const setView = (view: FRPState["view"]): SetView => ({
    type: FRPActionType.SetView,
    payload: { view }
})

export const setShownElementsIds = (shownElementsIds: FRPState["shownElementsIds"]): SetShownElementsIds => ({
    type: FRPActionType.SetShownElementsIds,
    payload: { shownElementsIds }
})

export const setSelectedElementIds = (selectedElementIds: FRPState["selectedElementIds"]): SetSelectedElementIds => ({
    type: FRPActionType.SetSelectedElementIds,
    payload: { selectedElementIds }
})

export const setFilter = (filter: FRPState["filter"]): SetFilter => ({
    type: FRPActionType.SetFilter,
    payload: { filter }
})

export const setVisualizationId = (visualizationId: FRPState["visualizationId"]): SetVisualizationId => ({
    type: FRPActionType.SetVisualizationId,
    payload: { visualizationId }
})

export const cleanProjectState = (): CleanProjectState => ({
    type: FRPActionType.CleanProjectState
})

export type FRPActionsTransActional =
    Initialize | LoadSFE | AddProject | UpdateProject | DeleteProject | SelectProject | LoadProjects | LoadProjectState;
export type FRPActionsNonTransActional = TxStarted | TxCompleted | TxFailed | AddElement | DeleteElement | DeleteStatus | AddStatus | UpdateStatus | SetStatus | SetTag | CleanProjectState | SetFilter | SetShownElementsIds | SetSelectedElementIds | SetView | SetVisualizationId | RevertAddProject  ;
export type FRPActions = FRPActionsTransActional | FRPActionsNonTransActional;

export interface ViewedElement {
    type: "ViewedElement";
    payload: string; // elementId
    date: Date;
}

export function viewedElement(elementId: string, date?: Date): ViewedElement {
    return {
        type: "ViewedElement",
        payload: elementId,
        date: date || new Date()
    };
}

export type FRPNotificationActions = ViewedElement

// ---------------------------------------------------- ACTIONS --------------------------------------------------------------



// ---------------------------------------------------- TRANSACTIONAL REDUCER --------------------------------------------------------------

export function transactionalFRPReducer(action: FRPActionsTransActional, dispatch: any) {

    switch (action.type) {
        case FRPActionType.Initialize: {
            if (action.payload.projectsElasticIndex) {
                let projectsPersistence = new FRPProjectPersistence(action.payload.projectsElasticIndex, action.payload.userId);
                FRPProjectPersistence.setInstance(projectsPersistence);
                let txId = MapitUtils.getNewUuid();
                dispatch(actionTxStarted(txId, action));
                FRPProjectPersistence.getInstance().listObjects()
                    .then((projects) => {
                        action.results = { projects: projects, projectsPersistence: projectsPersistence };
                        dispatch(actionTxCompleted(txId, action));
                        dispatch(action);
                    })
                    .catch((error) => {
                        console.log("Got error " + error);
                        dispatch(actionTxFailed(txId, action, "Error get projects: " + (error.message || error)));
                    })
            }
            break;
        }

        case FRPActionType.LoadSFE: {
            let sfeNummer = action.payload.sfeNummer;

            let txId = MapitUtils.getNewUuid();
            dispatch(actionTxStarted(txId, action));

            FRPElementPersistence.getInstance().getObject("" + sfeNummer)
                .then((data) => {
                    action.results = data;

                    dispatch(actionTxCompleted(txId, action));
                    dispatch(action);
                })
                .catch((error) => {
                    console.log("Got error get SFE data: " + error);
                    dispatch(actionTxFailed(txId, action, "Error get SFE data: " + (error.message || error)));
                })
            break;
        }

        case FRPActionType.LoadProjectState: {
            let txId = MapitUtils.getNewUuid();
            dispatch(actionTxStarted(txId, action));

            // load data from elastic search
            new Promise<void>((resolve, reject) => {
                try {
                    let bs = new FRPElementPersistence(action.payload.indexName, action.payload.userId!);
                    FRPElementPersistence.setInstance(bs);
                    bs.listObjects()
                        .then((data) => {
                            action.results = data;
                            resolve();
                        })
                        .catch((error) => {
                            console.log("Got error " + error);
                            reject(error);
                        });
                } catch (error) {
                    reject(error);
                }
            })
                .then(() => {
                    dispatch(actionTxCompleted(txId, action));
                    dispatch(action);
                })
                .catch((error) => {
                    console.log("Got error " + error);
                    dispatch(actionTxFailed(txId, action, "Error persist: " + (error.message || error)));
                });

            break;
        }

        case FRPActionType.LoadProjects: {
            let txId = MapitUtils.getNewUuid();
            dispatch(actionTxStarted(txId, action));
            let projectsPersistence = new FRPProjectPersistence(SettingsManager.getSystemSetting("frp.projectsElasticIndex", ""), "dummy, only for loading");
            FRPProjectPersistence.setInstance(projectsPersistence);
            FRPProjectPersistence.getInstance().listObjects().then((result) => {
                dispatch(actionTxCompleted(txId, action));
                action.results = result;
                dispatch(action);
            }).catch((error) => {
                console.log("Got error " + error);
                dispatch(actionTxFailed(txId, action, "Error persist: " + (error.message || error)));
            })
            break;
        }

        default:
            // proceed directly to non-transational reducer for other actions
            dispatch(action);
    }
}


// ---------------------------------------------------- REDUCER --------------------------------------------------------------

export function safeAddToDoubleDepthObject(topObject: { [key1: string]: { [key2: string]: any } }, key1: string, key2: string, value: any) {
    if (!topObject[key1]) {
        topObject[key1] = {};
    }
    if (!topObject[key1][key2]) {
        topObject[key1][key2] = {};
    }
    topObject[key1][key2] = value;
}

export function safeGetDoubleDepthObject(topObject: { [key1: string]: { [key2: string]: any } }, key1: string, key2: string): any {
    if (!topObject[key1]) {
        return null;
    }
    if (!topObject[key1][key2]) {
        return null;
    }
    return topObject[key1][key2];
}

export function FRPReducer(state: FRPState, action: FRPActions): FRPState {

    switch (action.type) {

        case FRPActionType.TxStarted:
            return {
                ...state,
                activeTransactions: { ...state.activeTransactions, [action.payload.guid]: action }
            };

        case FRPActionType.TxFailed: {
            let tmp = { ...state.activeTransactions };
            delete tmp[action.payload.guid];
            console.log(action.payload.error);
            let msg = Utils.formatString(/*Localization.getTextSafeMode(*/"Error. Transaction {txname} returned with {error}"/*)*/, {
                txname: /*Localization.getTextSafeMode(*/FRPActionType[action.payload.action.type]/*)*/,
                error: /*Localization.getTextSafeMode(*/action.payload.error/*)*/
            });
            // Logger.logError("viamapLiceningState", BNBOActionType[action.payload.action.type], msg);
            if ((action.payload.action as any).errorCallback) {
                (action.payload.action as any).errorCallback(action.payload.error);
            }
            return {
                ...state,
                message: msg,
                messageType: MessageType.Error,
                activeTransactions: tmp
            };
        }

        case FRPActionType.TxCompleted: {
            let tmp = { ...state.activeTransactions };
            delete tmp[action.payload.guid];
            return {
                ...state,
                activeTransactions: tmp,
                message: ""
            };
        }

        case FRPActionType.Initialize: {
            return {
                ...state,
                availableProjectsList: action.results ? action.results.projects : [],
                // projectsPersistence: action.results ? action.results.projectsPersistence : undefined,
                isInitialized: true
            };
            return state;
        }

        case FRPActionType.LoadProjectState: {
            if (action.results && action.results.length >= 0) {
                let { elements, messages, actions, childrenLookupTable } = decodeElementStatesFromPersistenceFormat(action.results);
                let newState = { ...state, elements, messages, actions, childrenLookupTable };
                let cleanedState = FRPReducer(newState, { type: FRPActionType.CleanProjectState });
                return { ...cleanedState };
            } else {
                return state;
            }
        }

        case FRPActionType.SelectProject: {
            let project = (state.availableProjectsList || []).find((p) => p.id === action.payload.id);
            if (project) {
                return {
                    ...state,
                    selectedProject: project,
                    selectedProjectId: action.payload.id,
                    statusOptions: project.settings?.statusOptions && project.settings.statusOptions.length > 0 ? project.settings.statusOptions : initialFRPState().statusOptions,
                    tagOptions: project.settings?.tagOptions && project.settings.tagOptions.length > 0 ? project.settings.tagOptions : initialFRPState().tagOptions,
                    view: "project",
                    selectedElementIds: [],
                    shownElementsIds: []
                }
            } else {
                return state;
            }
        }

        case FRPActionType.LoadProjects: {
            if (action.callback) {
                action.callback(action.results);
            };
            return { ...state, availableProjectsList: action.results }
        }

        case FRPActionType.UpdateProject: {
            return {
                ...state,
                selectedProject: action.payload.id === state.selectedProjectId ? action.payload.item : state.selectedProject,
                availableProjectsList: (state.availableProjectsList || []).map((p) => p.id === action.payload.id ? action.payload.item : p)
            }
        }

        case FRPActionType.AddProject: {
            return { ...state, availableProjectsList: (state.availableProjectsList || []).concat([action.payload.item]) }
        }

        case FRPActionType.RevertAddProject: {
            const list = state.availableProjectsList || [];
            const idx = list.findIndex((p) => p.id === action.payload.id);
            if (idx === -1) {
                return state;
            }
            return { ...state, availableProjectsList: [...list.slice(0, idx), ...list.slice(idx + 1)] };
        }

        case FRPActionType.DeleteProject: {
            return { ...state, availableProjectsList: (state.availableProjectsList || []).filter((p) => p.id !== action.payload.id) }
        }

        case FRPActionType.AddElement: {
            switch (action.payload.item.type) {
                case FRPElementType.Element:
                    let newElement = action.payload.item as FRPElement;
                    let oldElement = state.elements[newElement.id];


                    let clt = state.childrenLookupTable;
                    if (oldElement?.refElementId) {
                        clt = { ...clt, [oldElement.refElementId]: clt[oldElement.refElementId].filter(id => id !== action.payload.id) };
                    }
                    if (newElement.refElementId) {
                        clt = { ...clt, [newElement.refElementId]: [...clt[newElement.refElementId] ?? [], newElement.id] };
                    }

                    let updatedElements = { ...state.elements, [action.payload.id]: action.payload.item };

                    // If status is set and element has children, propagate status to all nested children
                    const statusChanged = oldElement?.status !== newElement.status;
                    if (statusChanged && newElement.status && clt[newElement.id]?.length > 0) {
                        const allNestedChildrenIds = FRPFunc.allNestedChildrenIds(newElement.id, clt);
                        allNestedChildrenIds.forEach(childId => {
                            const childElement = updatedElements[childId];
                            if (childElement) {
                                updatedElements[childId] = {
                                    ...childElement,
                                    status: newElement.status
                                };
                            }
                        });
                    }

                    return { ...state, childrenLookupTable: clt, elements: updatedElements }

                case FRPElementType.Message:
                    if (action.payload.item.edited) {
                        return { ...state, messages: state.messages.map((m) => m.id === action.payload.id ? action.payload.item as FRPMessage : m) }
                    }
                    return { ...state, messages: [...state.messages, action.payload.item as FRPMessage] }
                case FRPElementType.Action:
                    return { ...state, actions: [...state.actions, action.payload.item as FRPAction] }
                default:
                    console.warn("Unknown element type " + (action.payload.item as any).type + " for element " + action.payload.id);
                    return state;
            }
        }

        case FRPActionType.DeleteElement: {

            let idsToDelete = Array.isArray(action.payload.ids) ? action.payload.ids : [action.payload.ids];

            let updatedChildrenLookupTable = { ...state.childrenLookupTable };
            const remainingShownElementsIds = state.shownElementsIds.filter(id => !idsToDelete.includes(id));

            idsToDelete.forEach(id => {
                const element = state.elements[id];
                if (element?.refElementId) {
                    updatedChildrenLookupTable = {
                        ...updatedChildrenLookupTable,
                        [element.refElementId]: (updatedChildrenLookupTable[element.refElementId] || []).filter(childId => childId !== id)
                    };
                }
            });

            let directChildrenToUpdate: string[] = [];
            idsToDelete.forEach(id => {
                if (updatedChildrenLookupTable[id]) {
                    directChildrenToUpdate.push(...updatedChildrenLookupTable[id]);
                }
            });

            let updatedElements = { ...state.elements };
            directChildrenToUpdate.forEach(childId => {
                const childElement = updatedElements[childId];
                if (childElement && idsToDelete.includes(childElement.refElementId || "")) {
                    updatedElements[childId] = {
                        ...childElement,
                        refElementId: ""
                    };
                }
            });

            idsToDelete.forEach(id => {
                if (updatedChildrenLookupTable[id]) {
                    delete updatedChildrenLookupTable[id];
                }
            });

            updatedElements = Object.fromEntries(Object.entries(updatedElements).filter(([id]) => !idsToDelete.includes(id)));
            const updatedMessages = state.messages.filter((m) => !(idsToDelete.includes(m.elementId || "") || idsToDelete.includes(m.id)));
            const updatedActions = state.actions.filter((a) => !(idsToDelete.includes(a.elementId || "") || idsToDelete.includes(a.id)));
            const recalculatedShownElementsIds = remainingShownElementsIds.length > 0
                ? remainingShownElementsIds
                : FRPFunc.determineAllRootElements({ ...state, elements: updatedElements, childrenLookupTable: updatedChildrenLookupTable });

            return {
                ...state,
                childrenLookupTable: updatedChildrenLookupTable,
                elements: updatedElements,
                messages: updatedMessages,
                actions: updatedActions,
                shownElementsIds: recalculatedShownElementsIds
            }
        }



        case FRPActionType.DeleteStatus: {
            return { ...state, statusOptions: state.statusOptions.filter((s) => s.id !== action.payload.id) }
        }

        case FRPActionType.AddStatus: {
            const existingIds = state.statusOptions.map(s => parseInt(s.id)).filter(id => !isNaN(id));
            const nextId = existingIds.length > 0 ? Math.max(...existingIds) + 1 : 1;
            const newStatus = { ...action.payload.item, id: nextId.toString() };
            return { ...state, statusOptions: [...state.statusOptions, newStatus] }
        }

        case FRPActionType.UpdateStatus: {
            let filteredOptions = state.statusOptions.filter((s) => s.id !== action.payload.item.id);
            return { ...state, statusOptions: [...filteredOptions, action.payload.item] }
        }

        case FRPActionType.SetStatus: {
            return { ...state, statusOptions: action.payload.item }
        }

        case FRPActionType.SetTag: {
            let hueSort = (a, b) => (chroma(a.color).hsl()[0] || 1000 - chroma(b.color).hsl()[0] || 1000);
            return { ...state, tagOptions: action.payload.item.toSorted(hueSort) };
        }

        case FRPActionType.CleanProjectState: {
            const statusId = state.statusOptions.map((a) => a.id);
            const tagId = state.tagOptions.map((a) => a.id);
            const elementId = Object.keys(state.elements);

            let elementsToClean = Object.values(state.elements).map((a) => a);
            let cleanedElements = elementsToClean.map((a) => {
                return {
                    ...a,
                    tags: a.tags.filter((a) => tagId.includes(a)),
                    status: a.status && statusId.includes(a.status) ? a.status : "",
                    refElementId: a.refElementId && elementId.includes(a.refElementId) ? a.refElementId : ""
                };
            }).map((a) => [a.id, a])
            return { ...state, elements: Object.fromEntries(cleanedElements) };
        }

        case FRPActionType.SetView: {

            return { ...state, view: action.payload.view }
        }

        case FRPActionType.SetShownElementsIds: {
            return { ...state, shownElementsIds: action.payload.shownElementsIds && action.payload.shownElementsIds.length > 0 ? action.payload.shownElementsIds : FRPFunc.determineAllRootElements(state) }
        }

        case FRPActionType.SetSelectedElementIds: {
            return { ...state, selectedElementIds: action.payload.selectedElementIds }
        }

        case FRPActionType.SetFilter: {
            return { ...state, filter: action.payload.filter }
        }

        case FRPActionType.SetVisualizationId: {
            return { ...state, visualizationId: action.payload.visualizationId }
        }

        default:
            throw new Error("Unknown action " + (action as FRPActions).type);
    }
}

// ---------------------------------------------------- generic stuff --------------------------------------------------------------

export const FRPContext = React.createContext<{
    state: FRPState;
    dispatch: React.Dispatch<FRPActions>;
}>({
    state: initialFRPState(),
    dispatch: () => undefined,
});

export const FRPNotificationContext = React.createContext<{
    state: FRPNotificationState;
    dispatch: React.Dispatch<FRPNotificationActions>;
}>({
    state: initialNotificationFRPState(),
    dispatch: () => undefined,
});

export class GenericTransactionManager {

    static dispatchMiddleware<ActionTypes>(dispatch: any, transactionalReducer: (action: any, dispatch: any) => any) {

        return (action: ActionTypes) => {
            transactionalReducer(action, dispatch);
        };

    }
}

// ------- Database encode/decode ---------
export function encodeElementData(elementId: string, state: FRPState) {
    let element = state.elements[elementId];
    let centroidWkt: string | undefined = undefined;
    if (element.geometry) {
        try {
            let centroid = Turf.centroid(element.geometry);
            centroidWkt = wktStringify(centroid);
        } catch (e) {
            console.warn("Error calculating centroid for element " + elementId + ": " + e);
        }
    }
    return {
        id: element.id,
        elementId: element.id,
        refElementId: element.refElementId,
        type: element.type || "Element",
        status: element.status,
        element: JSON.stringify(element),
        geometry: element.geometry,
        geoshape: element.geometry ? wktStringify(element.geometry) : undefined,
        geopoint: centroidWkt,
        point: centroidWkt,
        messages: [],
        documents: {},
        lastChangedTimestamp: new Date(),
        lastChangedUser: undefined,
        tags: ["test", "example"]
    };
}

/**
 * @deprecated due to the fact that the data is stored in multiple places, depending on the type of element.
 */
function _decodeAndSaveOneElementToState(elementId: string, type: string, register: any, data: any) {
    let json = data && JSON.parse(data);
    if (json && type === json.type) {
        register[elementId] = json;
    }
}

export function encodeElementStateDataToPersistence(record: FRPElement | FRPMessage | FRPAction): any {
    return record
}

function decodeElementStatesFromPersistenceFormat(records: any[]) {

    let elements: FRPElementRegister = {};
    let messages: FRPMessageArray = [];
    let actions: FRPActionArray = [];
    let childrenLookupTable: { [parentRefId: string]: string[] } = {};
    for (let data of records) {
        if ("element" in data) {
            delete data.element;
        }
        if (data) {
            switch (data.type) {
                case FRPElementType.Element:
                    elements[data.id] = validFRPElement(data) as FRPElement;
                    break;
                case FRPElementType.Message:
                    messages.push(data as FRPMessage);
                    break;
                case FRPElementType.Action:
                    actions.push(data as FRPAction);
                    break;
                default:
                    console.warn("Unknown element type " + data.type + " for element " + data.id);
                    break;
            }
        }
    }

    let cleanedElements = removeCyclicReferences(elements);
    Object.values(cleanedElements).forEach((el) => {
        if (el.refElementId) {
            if (childrenLookupTable[el.refElementId] === undefined)
                childrenLookupTable[el.refElementId] = []
            childrenLookupTable[el.refElementId].push(el.id);
        }
    });
    return { elements: cleanedElements, messages, actions, childrenLookupTable };
}

function validFRPElement(data: any): FRPElement | null {
    let x = { ...data }
    if (x?.["tags"] == "_various") {
        console.error("Invalid tag value");
        x["tags"] = [];
    }

    return {
        status: "",
        tags: [],
        ...x
    };
}


// ---------------------------------------------------- Utility Functions --------------------------------------------------------------

export function getMyAccessRights(project: FRPProject, userRef: string, customerRef: string): string[] | undefined {
    let accessRights = project.allowedUsersWithRoles && project.allowedUsersWithRoles[userRef] || project.allowedOrganizationsWithRoles && project.allowedOrganizationsWithRoles[customerRef];
    return accessRights;
}

function removeCyclicReferences(elements: FRPElementRegister): FRPElementRegister {
    Object.values(elements).forEach((el) => {
        let visited = new Set<string>();
        let cyclicRef = findCyclicReference(el.id, elements, visited);
        if (cyclicRef) {
            console.warn("Cyclic reference found for element " + el.id + ": " + cyclicRef);
            elements[cyclicRef].refElementId = undefined;
        }
    });
    return elements;
}

function findCyclicReference(id, elements: FRPElementRegister, visited: Set<string>): string | null {
    if (visited.has(id)) {
        return id;
    }
    visited.add(id);
    let parentID = elements[id].refElementId;
    if (parentID && elements[parentID]) {
        return findCyclicReference(parentID, elements, visited);
    }
    return null;
}
